// ProcessCollectionModal — confirms collection completion for a job
// whose collection_status is 'requested'. Single-submit (no dual submit
// — collection is always immediate; the job is already queued).
//
// Wired to POST /api/v1/skip/jobs/:enc_id/complete-collection (existing).
// On success, the job's status moves to 'collected' and the active-actions
// query invalidates so the row leaves the list.

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Button } from '../../../../components/ui/Button/Button';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { apiClient } from '../../../../api/client';
import { endpoints } from '../../../../api/endpoints';
import { queryKeys } from '../../../../api/queryKeys';
import { extractErrorMessage } from '../../../../utils/errorMessage';

import styles from './ProcessActionModal.module.css';

export interface ProcessCollectionModalProps {
  isOpen: boolean;
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function ProcessCollectionModal({
  isOpen,
  jobEncId,
  jobNumber,
  customerName,
  onClose,
  onSuccess,
}: ProcessCollectionModalProps): JSX.Element | null {
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await apiClient.post(
        endpoints.skipJobs.action(jobEncId, 'complete-collection'),
        { notes: notes.trim() || undefined },
      );
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.all });
      void qc.invalidateQueries({ queryKey: queryKeys.skipJobs.detail(jobEncId) });
      onSuccess?.();
      onClose();
      setNotes('');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={submitting ? () => undefined : onClose}
      title="Mark Collected"
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={submitting}>
            Mark Collected
          </Button>
        </div>
      }
    >
      <p className={styles.body}>
        Confirm collection for <strong>{jobNumber}</strong> — {customerName}.
      </p>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="collection-notes">
          Collection notes (optional)
        </label>
        <textarea
          id="collection-notes"
          className={styles.textarea}
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </div>
      {error ? <p className={styles.error}>{error}</p> : null}
    </Modal>
  );
}
